// Authentication API client: login and token validation against /api/auth.
// Errors carry either `unreachable` (no usable answer from the server) or the
// HTTP `status` of a refusal, so AuthContext can keep a session through an outage.
const API_URL = process.env.REACT_APP_API_URL || '';
const TIMEOUT_MS = 15000;

const UNREACHABLE_MESSAGE = "The BatchMind server isn't responding. Please try again in a moment.";

// 5xx counts as unreachable too: the development proxy answers 502/504 while
// the Node server is down, as a reverse proxy does in front of it.
const unreachableError = (cause) => {
  const error = new Error(UNREACHABLE_MESSAGE);
  error.unreachable = true;
  if (cause) error.cause = cause;
  return error;
};

/**
 * Sends a request to the auth API and returns the parsed JSON body.
 * @param {string} path - API path, e.g. '/api/auth/login'.
 * @param {RequestInit} options - fetch options (method, headers, body).
 * @returns {Promise<Object>} The response body.
 * @throws {Error} With `unreachable: true` when no usable answer arrived
 *   (network failure, timeout, HTTP 5xx); otherwise with the refusal's `status`.
 */
const request = async (path, options) => {
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...options,
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
  } catch (error) {
    // fetch() only rejects when no response arrived: network down, server
    // stopped, or the timeout fired.
    console.error(`Request to ${path} failed:`, error);
    throw unreachableError(error);
  }
  if (response.status >= 500) throw unreachableError();
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    error.status = response.status;
    throw error;
  }
  return response.json();
};

const authService = {
  login: (username, password) => request('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  }),

  validateToken: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      const error = new Error("No authentication token found");
      error.status = 401;
      throw error;
    }
    const data = await request('/api/auth/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    return data.user;
  },
};

export default authService;
